import React from "react";
import { Badge, Box, Center, Flex, Text } from "@mantine/core";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import { VideoEvent } from "../../client";
import PlayerAvatar from "../Common/PlayerAvatar";
import { getEventPlayers } from "../../utils/player";
import { useHideOutcomes } from "../../hooks/useHideOutcomes";
import { getVideoLeague } from "../../utils/league";

export interface DefaultVideoOverlayProps {
  video: VideoEvent;
  compact?: boolean;
  showPlayButton?: boolean;
}

export const DefaultVideoOverlay: React.FC<DefaultVideoOverlayProps> = ({
  video,
  compact = false,
  showPlayButton = false,
}) => {
  const [hideOutcomes] = useHideOutcomes();
  const clipLeague = getVideoLeague(video);
  const matchup = video.vAbbv && video.hAbbv ? `${video.vAbbv} @ ${video.hAbbv}` : null;
  const eventTag = video.pta || (video.tags && video.tags.length > 0 ? video.tags[0] : null);
  const { primary } = getEventPlayers(video);

  return (
    <Box
      style={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(135deg, #1c1e24 0%, #121316 60%, #0a0b0d 100%)",
        zIndex: 0,
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      {/* Basketball Seams Watermark SVG (Zero network request, 100% reliable) */}
      <svg
        viewBox="0 0 100 100"
        style={{
          position: "absolute",
          right: compact ? "-15%" : "6%",
          top: "50%",
          transform: "translateY(-50%)",
          width: compact ? "65%" : "44%",
          height: "auto",
          opacity: 0.07,
          pointerEvents: "none",
        }}
        fill="none"
        stroke="white"
        strokeWidth="2.5"
      >
        <circle cx="50" cy="50" r="45" />
        <line x1="5" y1="50" x2="95" y2="50" />
        <line x1="50" y1="5" x2="50" y2="95" />
        <path d="M 18,18 C 35,35 35,65 18,82" />
        <path d="M 82,18 C 65,35 65,65 82,82" />
      </svg>

      {!compact ? (
        <>
          {/* Header Badges */}
          <Flex
            justify="space-between"
            align="center"
            p={10}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            {eventTag ? (
              <Badge
                size="xs"
                variant="filled"
                color="dark.5"
                radius="xs"
                className={`outcome-content ${hideOutcomes ? "outcome-blur" : ""}`}
              >
                {eventTag}
              </Badge>
            ) : (
              <Box />
            )}
            {matchup && (
              <Badge size="xs" variant="outline" color="gray.6" radius="xs">
                {matchup}
              </Badge>
            )}
          </Flex>

          {/* Bottom Player Card Info */}
          <Flex
            align="center"
            gap={10}
            style={{
              position: "absolute",
              left: 10,
              bottom: 10,
              right: 10,
              zIndex: 1,
            }}
          >
            <PlayerAvatar
              playerId={primary.id}
              playerName={primary.name}
              league={clipLeague}
              size={36}
              radius="xl"
            />
            <Box style={{ overflow: "hidden", flex: 1 }}>
              <Text size="xs" fw={700} c="white" lineClamp={1}>
                {primary.name}
              </Text>
              {video.desc && (
                <Text
                  size="10px"
                  c="dimmed"
                  lineClamp={1}
                  className={`outcome-content ${hideOutcomes ? "outcome-blur" : ""}`}
                >
                  {video.desc}
                </Text>
              )}
            </Box>
          </Flex>
        </>
      ) : (
        /* Compact Sidebar Queue Mode */
        <Center
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
          }}
        >
          <PlayerAvatar
            playerId={primary.id}
            playerName={primary.name}
            league={clipLeague}
            size={28}
            radius="xl"
          />
        </Center>
      )}

      {showPlayButton && (
        <Center
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            style={{
              width: compact ? 30 : 44,
              height: compact ? 30 : 44,
              borderRadius: "50%",
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            }}
          >
            <IconPlayerPlayFilled size={compact ? 14 : 20} />
          </Box>
        </Center>
      )}
    </Box>
  );
};
